/**
 * Custom Event API handler for B2M Tracker
 */
import { shouldDropEventParam } from '../utils/property-scope.js';

export default class CustomAPI {
  constructor(tracker) {
    this.tracker = tracker;
  }
  
  /**
   * Helper to create event property objects
   * @param {string} key - Property key
   * @param {any} value - Property value
   * @returns {Object} Property object
   */
  createEventProperty(key, value) {
    if (value === undefined || value === null) return null;
    return { key, value: String(value) };
  }

  /**
   * Resolve the app_identifier value written into user_params.
   *
   * Priority:
   *  1) Manual override from integration (`appIdentifier` / `data-app-identifier`)
   *     — free-form name, e.g. "checkout-app". Used when set (non-empty).
   *  2) Fallback (default for existing sites that never set it):
   *     - iframe → host page domain
   *     - else → window.location.hostname
   *
   * Empty / missing override must NOT change behaviour for current integrations.
   * @returns {string|null}
   */
  getAppIdentifier() {
    const configured = this.tracker.config.appIdentifier;
    if (typeof configured === 'string' && configured.length > 0) {
      return configured;
    }

    const b2mTracker = this.tracker.b2mTracker;
    if (b2mTracker && b2mTracker._isInIframe()) {
      const hostDomain = b2mTracker._getHostPageDomain();
      if (hostDomain && hostDomain !== 'unknown') return hostDomain;
    }

    if (typeof window !== 'undefined' && window.location && window.location.hostname) {
      return window.location.hostname;
    }
    return null;
  }

  /**
   * Custom user properties set through setUserProperties(). They are
   * persisted in `b2m_user_profile`, so we stamp them onto every event's
   * user_properties — this is how attributes like company_id / email /
   * application_id travel with all events, not only the one-off $identify.
   * @returns {Array<{key:string,value:string}>}
   */
  getProfileProperties() {
    const profile = this.tracker.storage.getItem('b2m_user_profile') || {};
    return Object.entries(profile).map(
      ([key, value]) => this.createEventProperty(key, value)
    );
  }

  sendEvents(useBeacon = false) {
    const queue = this.tracker.storage.getCustomQueue();
    
    if (queue.length === 0) {
      this.tracker.logger.log("No custom events to send");
      return;
    }
    
    // If offline, don't try to send (unless using beacon on page exit)
    if (!this.tracker.isOnline && !useBeacon) {
      this.tracker.logger.log("Device is offline, custom events will be sent when online");
      return;
    }
    
    // Get browser details for fingerprinting
    const browserInfo = this.tracker.browserInfo.getInfo();
    
    // Create a session ID for this batch if needed
    const sessionId = this.tracker.sessionId || this.generateSessionId();
    
    // ANALYTICS-6897: record every value's shape across the WHOLE batch before
    // converting, so the first event of a batch gets the same dtype as the rest.
    const dtypeRegistry = this.tracker.config.autoPropertyTypes ? this.tracker.propertyDtypes : null;
    if (dtypeRegistry) {
      try {
        const eu = this.tracker.eventUtils;
        const declared = this.tracker.config.propertyTypes;
        queue.forEach((ev) => Object.keys(ev).forEach((key) => {
          if (key.startsWith('_')) return;
          if (shouldDropEventParam(key, ev.event)) return;
          if (key === 'items' && Array.isArray(ev[key])) return;
          dtypeRegistry.observe(key, eu.enforceType(key, eu.coerceValue(ev[key]), declared), declared);
        }));
      } catch (e) { /* never block a send on dtype bookkeeping */ }
    }

    // Transform queue to match the bulk API format
    const formattedEvents = queue.map((event) => {
      // Determine the data source
      const interceptorName = "b2m";
      
      // Create event_params array from all properties in the event
      const eventParams = [];
      
      // Map all properties from the event object to event_params array
      Object.keys(event).forEach(key => {
        // Skip internal properties that start with underscore like _timestamp
        if (key.startsWith('_')) return;
        // User-level keys travel in user_properties only (ANALYTICS-6725).
        if (shouldDropEventParam(key, event.event)) return;

        const val = event[key];

        // Flatten items array — each item's properties become separate params (item_0_item_id, etc.)
        if (key === 'items' && Array.isArray(val)) {
          val.forEach((item, index) => {
            const itemObj = typeof item === 'string' ? (() => { try { return JSON.parse(item); } catch(e) { return null; } })() : item;
            if (itemObj && typeof itemObj === 'object') {
              Object.keys(itemObj).forEach(itemKey => {
                const coerced = this.tracker.eventUtils.coerceValue(itemObj[itemKey]);
                if (coerced !== null) {
                  eventParams.push({ key: `item_${index}_${itemKey}`, value: coerced });
                }
              });
            }
          });
          return;
        }

        let coerced = this.tracker.eventUtils.coerceValue(val);
        coerced = this.tracker.eventUtils.enforceType(key, coerced, this.tracker.config.propertyTypes);
        // Undeclared keys: one stable dtype per property name (ANALYTICS-6897).
        if (this.tracker.propertyDtypes && this.tracker.config.autoPropertyTypes) {
          coerced = this.tracker.propertyDtypes.apply(key, coerced, this.tracker.config.propertyTypes);
        }
        if (coerced !== null) {
          eventParams.push({ key, value: coerced });
        }
      });
      
      // Add interceptor information (same as Google Analytics uses)
      eventParams.push({
        key: "interceptor",
        value: interceptorName
      });
      
      return {
        event_name: event.event || "custom_event",
        event_date: event._timestamp || new Date().toISOString(),
        event_timestamp: Math.floor(Date.now() / 1000),
        event_params: eventParams,
        session_id: event._sessionId || sessionId,
        user_first_timestamp: this.tracker.storage.getFirstTimestamp(),
        user_id: event._userId || this.tracker.userId.getId(),
        user_properties: [
          this.createEventProperty('app_identifier', this.getAppIdentifier()),
          // Device fingerprint stays stable even after setUserId — the
          // logged-in identity lives in user_id, not here.
          this.createEventProperty('fingerprint_id', this.tracker.userId.getFingerprintId()),
          // Device-CLASS hash: stitching hint for the backend, never an identity.
          this.tracker.userId.getDeviceClassHash() &&
            this.createEventProperty('device_class_hash', this.tracker.userId.getDeviceClassHash()),
          (event._userId ? event._idIsTemporary : this.tracker.userId.isIdTemporary()) && this.createEventProperty('id_status', 'temporary'),
          this.createEventProperty('user_agent', browserInfo.user_agent),
          this.createEventProperty('language', browserInfo.language),
          // SDK surface: "Web" (desktop) / "M-Web" (mobile|tablet browser).
          this.createEventProperty('platform', this.tracker.browserInfo.getSdkPlatform()),
          // OS from UA parse (Windows / macOS / Linux / iOS…) — kept separate from platform.
          this.createEventProperty('os', this.tracker.browserInfo.getAutoProperties().os),
          this.createEventProperty('screen_resolution', browserInfo.screen.split('x').slice(0, 2).join('x')),
          this.createEventProperty('color_depth', browserInfo.screen.split('x')[2]),
          this.createEventProperty('timezone', browserInfo.timezone),
          this.createEventProperty('timezone_offset', browserInfo.timezone_offset),
          browserInfo.hardware_concurrency && this.createEventProperty('hardware_concurrency', browserInfo.hardware_concurrency),
          browserInfo.device_memory && this.createEventProperty('device_memory', browserInfo.device_memory),
          this.createEventProperty('is_touch_capable', browserInfo.touch_support),
          browserInfo.webgl_vendor_renderer && this.createEventProperty('webgl_vendor', browserInfo.webgl_vendor_renderer),
          browserInfo.hash_of_canvas_fingerprint && this.createEventProperty('canvas_hash', browserInfo.hash_of_canvas_fingerprint),
          ...this.tracker.getAttributionUserParams().map(p => this.createEventProperty(p.key, p.value)),
          ...this.getProfileProperties()
        ].filter(Boolean)
      };
    });
    
    const payload = JSON.stringify(formattedEvents);
    
    const headers = {
      "Content-Type": "application/json",
      "x-api-key": this.tracker.config.apiKey
    };
    
    this.tracker.logger.log("Sending custom events via bulk API:", formattedEvents);
    
    // Use sendBeacon for unload events
    if (useBeacon && navigator.sendBeacon) {
      try {
        // Create blob with JSON data
        const blob = new Blob([payload], { type: 'application/json' });
        
        // Send using beacon API
        const success = navigator.sendBeacon(this.tracker.config.apiUrl, blob);
        
        if (success) {
          this.tracker.logger.log("Custom events sent successfully via Beacon");
          this.tracker.storage.removeFromCustomQueue(queue.length);
        } else {
          this.tracker.logger.error("Failed to send custom events via Beacon");
        }
      } catch (e) {
        this.tracker.logger.error("Error sending custom events via Beacon:", e);
      }
      return;
    }
    
    // Otherwise use fetch API
    fetch(this.tracker.config.apiUrl, {
      method: "POST",
      headers: headers,
      body: payload
    })
    .then(response => {
      if (!response.ok) {
        throw new Error("Server returned " + response.status);
      }
      
      // Check if response has content
      const contentType = response.headers.get("content-type");
      if (contentType && contentType.includes("application/json")) {
        return response.json().catch(() => {
          // If JSON parsing fails, return empty object
          this.tracker.logger.log("Response is not valid JSON, but status is OK");
          return {};
        });
      } else {
        // No JSON content, return empty object
        this.tracker.logger.log("Response has no JSON content, but status is OK");
        return Promise.resolve({});
      }
    })
    .then(data => {
      this.tracker.logger.log("Custom events sent successfully:", data);
      // Remove only the transmitted batch — keep events queued mid-flight.
      this.tracker.storage.removeFromCustomQueue(queue.length);
      this.tracker.retryCount = 0;
    })
    .catch(error => {
      this.tracker.logger.error("Error sending custom events:", error);
      
      // Check if this is a permanent error (4xx errors except 429)
      const isPermanentError = error.message && (
        error.message.includes("401") ||  // Unauthorized
        error.message.includes("403") ||  // Forbidden
        error.message.includes("404") ||  // Not Found
        error.message.includes("400")     // Bad Request
      );
      
      // Clear queue for permanent errors only
      if (isPermanentError) {
        this.tracker.logger.log("Dropping rejected batch due to permanent error");
        this.tracker.storage.removeFromCustomQueue(queue.length);
        this.tracker.retryCount = 0;
        return;
      }
      
      // Implement retry logic for temporary errors only
      if (this.tracker.retryCount < this.tracker.config.maxRetries) {
        this.tracker.retryCount++;
        
        setTimeout(() => {
          this.tracker.logger.log("Retrying custom event send. Attempt " + this.tracker.retryCount);
          this.sendEvents();
        }, this.tracker.config.retryTimeout);
      } else {
        // Max retries reached - clear queue to prevent infinite accumulation
        this.tracker.logger.log("Max retries reached, dropping unsendable batch");
        this.tracker.storage.removeFromCustomQueue(queue.length);
        this.tracker.retryCount = 0;
      }
    });
  }
  
  /**
   * Get or generate a session ID - maintains consistency across requests
   * @returns {string} Session ID
   */
  generateSessionId() {
    // Check if we have a valid existing session
    const existingSessionId = this.tracker.storage.getSessionId();
    if (existingSessionId && !this.tracker.storage.isSessionExpired(this.tracker.config.sessionTimeout)) {
      return existingSessionId;
    }
    
    // Generate new session
    const timestamp = Date.now();
    const randomPart = Math.floor(Math.random() * 1000000);
    const userIdFragment = this.tracker.userId.getId().substring(0, 8);
    
    let sessionId;
    
    // Use custom session parameters if available
    if (this.tracker.config.sessionParams) {
      const params = this.tracker.config.sessionParams;
      sessionId = params.prefix || 'session';
      
      if (params.includeTimestamp) {
        sessionId += `_${timestamp}`;
      }
      
      if (params.includeUserId) {
        sessionId += `_${userIdFragment}`;
      }
      
      if (params.includeRandom) {
        sessionId += `_${randomPart}`;
      }
    } else {
      // Default format
      sessionId = `session_${timestamp}_${userIdFragment}_${randomPart}`;
    }
    
    // Store the new session
    this.tracker.storage.setSessionId(sessionId);
    this.tracker.storage.setSessionStartTime(timestamp);
    
    return sessionId;
  }
} 
